// The public links a workspace's members make onto its assets: the link, what it serves, who was
// emailed it and what it has done. Reading a link takes a seat in its workspace; every write — and
// the email history, which carries each recipient's personal link — takes `assets.asset.share`
// there, and the figures `assets.asset.view_analytics`.

import { api, withQuery } from '../client'
import type { components, operations } from '../schema'

export type ShareView = components['schemas']['ShareView']
export type SharePage = components['schemas']['Page_ShareView']
export type ShareStatus = components['schemas']['ShareStatus']
export type CreateShareRequest = components['schemas']['CreateShareRequest']
export type UpdateShareRequest = components['schemas']['UpdateShareRequest']
export type ShareAssetEntry = components['schemas']['ShareAssetEntry']
export type AddShareAssetsRequest = components['schemas']['AddShareAssetsRequest']
export type ShareAssetsAdded = components['schemas']['ShareAssetsAdded']
export type SendShareEmailsRequest = components['schemas']['SendShareEmailsRequest']
export type ShareEmailSendEntry = components['schemas']['ShareEmailSendEntry']
export type ShareRecipientEntry = components['schemas']['ShareRecipientEntry']
export type ShareStatistics = components['schemas']['ShareStatistics']
/** The filters and the walk: `workspace_id`, `status`, `contains_asset`, `limit`, `after`, `order`. */
export type SharesQuery = NonNullable<operations['shares_list']['parameters']['query']>

function sharePath(shareId: string): string {
  return `/shares/${encodeURIComponent(shareId)}`
}

/**
 * One page of the links on every workspace the caller holds a seat in — not only the ones they
 * made: a link belongs to its workspace and outlives its author. Oldest first unless `order` is
 * `desc`; a page carries no total.
 */
export function listShares(query: SharesQuery = {}, signal?: AbortSignal): Promise<SharePage> {
  return api.get<SharePage>(withQuery('/shares', query), undefined, signal)
}

/**
 * Make a link onto at least one asset placed on `workspace_id`. The answer carries the token; the
 * page a visitor opens is the SPA's `/share/{token}`, which the client builds. 400 names an asset
 * that is not placed on that workspace.
 */
export function createShare(request: CreateShareRequest): Promise<ShareView> {
  return api.post<ShareView>('/shares', request)
}

/** One link and its counters. */
export function getShare(shareId: string, signal?: AbortSignal): Promise<ShareView> {
  return api.get<ShareView>(sharePath(shareId), undefined, signal)
}

/**
 * Change a link. `password`, `expires_at` and `max_views` take an explicit `null` to take them
 * away; withdrawing a link and putting it back is `status`, not a delete.
 */
export function updateShare(shareId: string, request: UpdateShareRequest): Promise<ShareView> {
  return api.patch<ShareView>(sharePath(shareId), request)
}

/** Withdraw a link for good: its token is freed, and its contents and email history go with it. */
export function deleteShare(shareId: string): Promise<undefined> {
  return api.del<undefined>(sharePath(shareId))
}

/** What a link names, in the order a visitor is shown them. A folder is one entry. */
export function listShareAssets(shareId: string, signal?: AbortSignal): Promise<ShareAssetEntry[]> {
  return api.get<ShareAssetEntry[]>(`${sharePath(shareId)}/assets`, undefined, signal)
}

/** Append assets to a link. One it already names is skipped and listed in `already_present`. */
export function addShareAssets(shareId: string, request: AddShareAssetsRequest): Promise<ShareAssetsAdded> {
  return api.post<ShareAssetsAdded>(`${sharePath(shareId)}/assets`, request)
}

/** Take one asset out of a link; the asset itself is untouched. 404 when the link does not name it. */
export function removeShareAsset(shareId: string, assetId: string): Promise<undefined> {
  return api.del<undefined>(`${sharePath(shareId)}/assets/${encodeURIComponent(assetId)}`)
}

/**
 * Every batch the link was emailed in, newest first, each recipient with their own token.
 * Delivery, bounce, open and click counts read zero — nothing reports them yet; each recipient's
 * `view_count` is live.
 */
export function listShareEmails(shareId: string, signal?: AbortSignal): Promise<ShareEmailSendEntry[]> {
  return api.get<ShareEmailSendEntry[]>(`${sharePath(shareId)}/emails`, undefined, signal)
}

/**
 * Email the link to up to 100 addresses, each with a personal link of their own. A 201 means the
 * messages are queued, not delivered; 503 when this server has no mail relay.
 */
export function sendShareEmails(shareId: string, request: SendShareEmailsRequest): Promise<ShareEmailSendEntry> {
  return api.post<ShareEmailSendEntry>(`${sharePath(shareId)}/emails`, request)
}

/**
 * What a link has done: its counters, the three refusals evaluated, and the ten most recent visits.
 * No watch time, completion, devices or countries — those are the analytics tier's.
 */
export function getShareStatistics(shareId: string, signal?: AbortSignal): Promise<ShareStatistics> {
  return api.get<ShareStatistics>(`${sharePath(shareId)}/statistics`, undefined, signal)
}
